import { formatForDatetimeLocalInput, getOrganizationTimezone } from '../../lib/timezoneUtils';
export type EventKind = 'job' | 'task' | 'work_order' | 'maintenance' | 'meeting';
export interface DispatchEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  kind: EventKind;
  record: any;
  assignee: string;
  assigneeName: string;
  job: string;
  status: string;
  address: string;
  href: string;
}
export const localDay = (value: string) => {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};
export const dayAfter = (value: string) => {
  const d = localDay(value);
  d.setDate(d.getDate() + 1);
  return d;
};
export const displayTime = (value: string, zone = getOrganizationTimezone()) =>
  /[zZ]|[+-]\d{2}:?\d{2}$/.test(value)
    ? new Date(formatForDatetimeLocalInput(new Date(value), zone))
    : new Date(value);
export function toEvent(kind: EventKind, r: any, zone = getOrganizationTimezone()): DispatchEvent | null {
  const raw =
    kind === 'job'
      ? r.build_date
      : kind === 'task'
        ? r.due_date
        : kind === 'work_order'
          ? r.start_date
          : kind === 'meeting'
            ? r.start_time
            : r.start;
  if (!raw) return null;
  const allDay = kind === 'job' || kind === 'work_order';
  const start = allDay ? localDay(raw) : displayTime(raw, zone);
  const end = allDay
    ? dayAfter(kind === 'work_order' ? r.end_date : raw)
    : kind === 'task'
      ? new Date(start.getTime() + 30 * 60000)
      : displayTime(kind === 'meeting' ? r.end_time : r.end, zone);
  const assigned = kind === 'meeting' ? r.host : r.assigned_to;
  const user = typeof assigned === 'object' ? assigned?.id : assigned;
  const name =
    kind === 'work_order'
      ? r.subcontractor_name
      : typeof assigned === 'object'
        ? `${assigned?.first_name || ''} ${assigned?.last_name || ''}`.trim()
        : r.assigned_to_name || r.host_name;
  return {
    id: `${kind}-${r.id}`,
    kind,
    record: r,
    start,
    end,
    allDay,
    title:
      kind === 'job'
        ? `${r.job_number || 'Job'} · ${r.address_1}`
        : r.title || r.name || 'Appointment',
    assignee:
      kind === 'work_order' ? `crew-${r.subcontractor}` : user ? `user-${user}` : 'unassigned',
    assigneeName: name || (user ? `User ${user}` : 'Unassigned'),
    job: String(
      kind === 'job'
        ? r.id
        : typeof r.related_job === 'object'
          ? r.related_job?.id
          : r.related_job || r.job || '',
    ),
    status: r.status || r.board_status_name || '',
    address:
      kind === 'job'
        ? [r.address_1, r.city, r.state].filter(Boolean).join(', ')
        : r.property_address || r.address || '',
    href:
      kind === 'job'
        ? `/operations/jobs/${r.id}`
        : kind === 'task'
          ? `/operations/tasks?task=${r.id}`
          : kind === 'work_order'
            ? `/operations/jobs/${r.job}?tab=work-orders`
            : kind === 'maintenance'
              ? `/maintenance/appointments/${r.id}`
              : '/scheduling/meetings',
  };
}
export const overlaps = (a: DispatchEvent, b: DispatchEvent) =>
  a.id !== b.id &&
  a.assignee !== 'unassigned' &&
  a.assignee === b.assignee &&
  a.start < b.end &&
  a.end > b.start;
